import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useCart } from "../contexts/CartContext";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../config";
import useTitle from "../hooks/useTitle";

export default function CheckoutPage() {
  useTitle("Checkout | Astro Portal");
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);

  const [form, setForm] = useState({
    billing_name: "",
    billing_phone: "",
    billing_street: "",
    billing_city: "",
    billing_pincode: "",
    billing_state: "",
    billing_country: "India",
    shipping_name: "",
    shipping_phone: "",
    shipping_street: "",
    shipping_city: "",
    shipping_pincode: "",
    shipping_state: "",
    shipping_country: "India",
    payment_method: "COD",
    shipping_method: "Standard Delivery",
    shipping_charge: 0,
    order_notes: "",
  });
  const SHIPPING_CHARGES = {
    Standard: 50,
    Express: 120,
    "Next Day": 200,
    "Store Pickup": 0,
    Courier: 80,
    Free: 0,
  };
  const [gateways, setGateways] = useState([]);
  const authData = JSON.parse(sessionStorage.getItem("authSession"));
  const token = authData.jwt_token;
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/payment-gateways`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          setGateways(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching payment gateways:", err));
  }, []);
  // ✅ Redirect if not logged in
  useEffect(() => {
    const authUser = JSON.parse(sessionStorage.getItem("authSession"));
    if (!authUser) {
      navigate("/login");
      return;
    }
    setUser(authUser);
  }, [navigate]);

  useEffect(() => {
    const charge = SHIPPING_CHARGES[form.shipping_method] ?? 0;
    setForm((prev) => ({ ...prev, shipping_charge: charge }));
  }, [form.shipping_method]);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      toast.warning("Please enter a coupon code.");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE_URL}/apply-coupon`,
        {
          coupon_code: couponCode,
          amount: subtotal, // base amount
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.success) {
        setDiscount(res.data.discount); // backend returns discount
        toast.success(res.data.message || "Coupon applied!");
      } else {
        setDiscount(0);
        toast.error(res.data.message || "Invalid coupon.");
      }
    } catch (err) {
      console.error("Coupon Error:", err);
      toast.error("Something went wrong applying coupon.");
    }
  };

  const finalTotal =
    subtotal - Number(discount || 0) + Number(form.shipping_charge || 0);

  // ✅ Handle Order Submit
  const handlePlaceOrder = async () => {
    // 🔒 Ensure user logged in
    if (!authData || !authData.jwt_token || !authData.user) {
      navigate("/login?redirect=/checkout");
      return;
    }
    const user = authData.user;

    if (items.length === 0) {
      toast.warning("Your cart is empty.");
      return;
    }

    // Validate minimal fields
    if (!form.billing_name || !form.billing_phone || !form.billing_city) {
      toast.warning("Please fill all billing details.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/checkout`,
        {
          amount: subtotal,
          total_amount: subtotal - Number(discount || 0) + Number(form.shipping_charge || 0),
          discount: Number(discount || 0),
          billing_name: form.billing_name,
          billing_phone: form.billing_phone,
          billing_street: form.billing_street,
          billing_city: form.billing_city,
          billing_pincode: form.billing_pincode,
          billing_state: form.billing_state,
          billing_country: form.billing_country,
          shipping_name: form.shipping_name || form.billing_name,
          shipping_phone: form.shipping_phone || form.billing_phone,
          shipping_street: form.shipping_street || form.billing_street,
          shipping_city: form.shipping_city || form.billing_city,
          shipping_pincode: form.shipping_pincode || form.billing_pincode,
          shipping_state: form.shipping_state || form.billing_state,
          shipping_country: form.shipping_country || form.billing_country,
          payment_method: form.payment_method,
          shipping_method: form.shipping_method,
          shipping_charge: Number(form.shipping_charge || 0),
          order_notes: form.order_notes,
          products: JSON.stringify(
            items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
            }))
          ),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Order placed successfully!");
        clearCart();
        navigate("/orders");
      } else {
        toast.error(response.data.message || "Order failed");
      }
    } catch (error) {
      console.error("Order Error:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired, please log in again.");
        sessionStorage.removeItem("authSession");
        navigate("/login");
      } else {
        toast.error("Something went wrong, please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="container my-5" style={{ maxWidth: "900px" }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-4"
        style={{
          background: "#f0f0f3",
          boxShadow: "8px 8px 16px #bebebe, -8px -8px 16px #ffffff",
        }}
      >
        <h3 className="fw-bold mb-4">🛍 Checkout</h3>

        <div className="row g-3">
          {/* Billing Info */}
          <div className="col-md-6">
            <h5 className="fw-semibold mb-3 text-primary">Billing Details</h5>
            {[
              "billing_name",
              "billing_phone",
              "billing_street",
              "billing_city",
              "billing_pincode",
              "billing_state",
              "billing_country",
            ].map((field) => (
              <div key={field} className="mb-2">
                <label className="form-label text-muted text-capitalize">
                  {field.replace("billing_", "").replace("_", " ")}
                </label>
                <input
                  type="text"
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  className="form-control rounded-pill shadow-sm"
                />
              </div>
            ))}
          </div>

          {/* Shipping Info */}
          <div className="col-md-6">
            <h5 className="fw-semibold mb-3 text-primary">Shipping Details</h5>
            {[
              "shipping_name",
              "shipping_phone",
              "shipping_street",
              "shipping_city",
              "shipping_pincode",
              "shipping_state",
              "shipping_country",
            ].map((field) => (
              <div key={field} className="mb-2">
                <label className="form-label text-muted text-capitalize">
                  {field.replace("shipping_", "").replace("_", " ")}
                </label>
                <input
                  type="text"
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  className="form-control rounded-pill shadow-sm"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Payment & Notes */}
        <div className="row mt-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold text-secondary">
              Payment Method
            </label>
            <select
              name="payment_method"
              value={form.payment_method}
              onChange={handleChange}
              className="form-select rounded-pill shadow-sm"
            >
              <option value="">Select Payment Method</option>
              {/* Dynamic Gateways from DB */}
              {gateways.map((gateway) => (
                <option key={gateway.id} value={gateway.id}>
                  {gateway.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold text-secondary">
              Shipping Method
            </label>
            <select
              name="shipping_method"
              value={form.shipping_method}
              onChange={handleChange}
              className="form-select rounded-pill shadow-sm"
            >
              <option value="">-- Select Shipping Method --</option>
              <option value="Standard">Standard Shipping (4–7 days)</option>
              <option value="Express">Express Shipping (1–3 days)</option>
              <option value="Next Day">Next Day Delivery</option>
              <option value="Store Pickup">Store Pickup</option>
              <option value="Courier">Courier Service</option>
              <option value="Free">Free Shipping</option>
            </select>
          </div>

          <div className="col-12 mt-3">
            <label className="form-label fw-semibold text-secondary">
              Order Notes
            </label>
            <textarea
              name="order_notes"
              value={form.order_notes}
              onChange={handleChange}
              rows="3"
              className="form-control rounded-4 shadow-sm"
              placeholder="Any special instructions?"
            />
          </div>
        </div>

        {/* Coupon Section */}
        <div className="mt-3 d-flex">
          <input
            type="text"
            className="form-control rounded-pill me-2"
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <button
            className="btn btn-dark rounded-pill px-3"
            onClick={handleApplyCoupon}
          >
            Apply
          </button>
        </div>

        {discount > 0 && (
          <div className="d-flex justify-content-between mt-2 text-success fw-bold">
            <span>Discount</span>
            <span>- ₹{Number(discount).toFixed(2)}</span>
          </div>
        )}

        {/* Summary */}
        <div
          className="mt-4 p-3 rounded-4"
          style={{
            background: "#fff",
            boxShadow: "4px 4px 10px #ccc, -4px -4px 10px #fff",
          }}
        >
          <h5 className="fw-bold">Order Summary</h5>
          <div className="d-flex justify-content-between mt-2">
            <span>Subtotal</span>
            <strong>₹{subtotal.toFixed(2)}</strong>
          </div>
          <div className="d-flex justify-content-between mt-2">
            <span>Shipping</span>
            <strong>₹{Number(form.shipping_charge || 0).toFixed(2)}</strong>
          </div>
          <div className="d-flex justify-content-between mt-2">
            <span>Discount</span>
            <strong>₹{discount.toFixed(2)}</strong>
          </div>
          <hr />
          <div className="d-flex justify-content-between fw-bold">
            <span>Total</span>
            <span style={{ color: "#d32f2f" }}>
              ₹{finalTotal.toFixed(2)}
              {/* ₹{(subtotal + Number(form.shipping_charge || 0)).toFixed(2)} */}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-4 text-end">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
            onClick={handlePlaceOrder}
            className="btn fw-bold rounded-pill px-4"
            style={{
              background: "linear-gradient(145deg, #43e97b, #38f9d7)",
              color: "white",
              boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
            }}
          >
            {loading ? "Placing Order..." : "Place Order"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
